<template>
  <div class="card">
    <div class="card-header" aria-label="Bank Details Header">
      <h3 class="card-title">
        {{ $t("bankDetails") }}
      </h3>
    </div>
    <div class="card-body" aria-label="Bank Details Body">
      <div class="w-100">
        <div
          v-for="(bankDetails, index) in bankDetailsList"
          :key="index"
          class="mb-3"
        >
          <b-row>
            <b-col cols="12" lg="4" md="4" class="mb-1">
              <div class="form-group">
                <label class="form-label input-label" for="country"
                  >{{ $t("country") }}&nbsp;<span style="color: red"
                    >*</span
                  ></label
                >
                <validation-provider
                  #default="{ errors }"
                  name="Country"
                  rules="required"
                >
                  <multi-select
                    :multiple="false"
                    label="name"
                    track-by="name"
                    :options="countries"
                    :key="countries"
                    v-model="bankDetails.country"
                    :class="{ 'is-invalid': errors[0] }"
                    :disabled="isDisabled"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </div>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12" lg="4" md="4" class="mb-1">
              <validation-provider
                #default="{ errors }"
                name="Bank Name"
                rules="required"
              >
                <text-input
                  v-model="bankDetails.bankName"
                  :label="$t('bankName')"
                  :required="true"
                  :maxLength="100"
                  aria-label="Bank Name Input"
                  :error="errors[0]"
                  :disabled="isDisabled"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>
            <b-col cols="12" lg="4" md="4" class="mb-1">
              <validation-provider
                #default="{ errors }"
                name="BIC/SWIFT"
                rules="required"
              >
                <text-input
                  v-model="bankDetails.swift"
                  :label="$t('BIC/SWIFT')"
                  :required="true"
                  :maxLength="100"
                  aria-label="BIC/Swift Input"
                  :error="errors[0]"
                  :disabled="isDisabled"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>
            <b-col
              v-if="getBankFields(index).includes('iban')"
              cols="12"
              lg="4"
              md="4"
              class="mb-1"
            >
              <validation-provider
                #default="{ errors }"
                name="IBAN"
                rules="required"
              >
                <text-input
                  v-model="bankDetails.iban"
                  :label="$t('IBAN')"
                  :required="true"
                  :maxLength="100"
                  :error="errors[0]"
                  :disabled="isDisabled"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>

            <b-col
              v-if="getBankFields(index).includes('account_number')"
              cols="12"
              lg="4"
              md="4"
              class="mb-1"
            >
              <validation-provider
                #default="{ errors }"
                name="Account Number"
                rules="required"
              >
                <text-input
                  v-model="bankDetails.account_number"
                  :label="$t('accountNumber')"
                  :required="true"
                  :maxLength="100"
                  :error="errors[0]"
                  :disabled="isDisabled"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>
            <b-col
              v-if="getBankFields(index).includes('routing_number')"
              cols="12"
              lg="4"
              md="4"
              class="mb-1"
            >
              <validation-provider
                #default="{ errors }"
                name="Routing Number"
                rules="required"
              >
                <text-input
                  v-model="bankDetails.routing_number"
                  :label="$t('routingNumber')"
                  :required="true"
                  :maxLength="100"
                  :error="errors[0]"
                  :disabled="isDisabled"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>
            <b-col
              v-if="getBankFields(index).includes('institution_number')"
              cols="12"
              lg="4"
              md="4"
              class="mb-1"
            >
              <validation-provider
                #default="{ errors }"
                name="Institution Number"
                rules="required"
              >
                <text-input
                  v-model="bankDetails.institution_number"
                  :label="$t('institutionNumber')"
                  :required="true"
                  :maxLength="100"
                  :error="errors[0]"
                  :disabled="isDisabled"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>
            <b-col
              v-if="getBankFields(index).includes('transit_number')"
              cols="12"
              lg="4"
              md="4"
              class="mb-1"
            >
              <validation-provider
                #default="{ errors }"
                name="Transit Number"
                rules="required"
              >
                <text-input
                  v-model="bankDetails.transit_number"
                  :label="$t('transitNumber')"
                  :required="true"
                  :maxLength="100"
                  :error="errors[0]"
                  :disabled="isDisabled"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>
            <b-col
              v-if="getBankFields(index).includes('bsb_code')"
              cols="12"
              lg="4"
              md="4"
              class="mb-1"
            >
              <validation-provider
                #default="{ errors }"
                name="BSB Code"
                rules="required"
              >
                <text-input
                  v-model="bankDetails.bsb_code"
                  :label="$t('bSBCode')"
                  :required="true"
                  :maxLength="100"
                  :error="errors[0]"
                  :disabled="isDisabled"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>
            <b-col
              v-if="getBankFields(index).includes('branch_code')"
              cols="12"
              lg="4"
              md="4"
              class="mb-1"
            >
              <validation-provider
                #default="{ errors }"
                name="Branch Code"
                rules="required"
              >
                <text-input
                  v-model="bankDetails.branch_code"
                  :label="$t('branchCode')"
                  :required="true"
                  :maxLength="100"
                  :error="errors[0]"
                  :disabled="isDisabled"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider> </b-col
            ><b-col
              v-if="getBankFields(index).includes('bank_code')"
              cols="12"
              lg="4"
              md="4"
              class="mb-1"
            >
              <validation-provider
                #default="{ errors }"
                name="Bank Code"
                rules="required"
              >
                <text-input
                  v-model="bankDetails.bank_code"
                  :label="$t('bankCode')"
                  :required="true"
                  :maxLength="100"
                  :error="errors[0]"
                  :disabled="isDisabled"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>
            <div
              v-if="!isDisabled"
              role="button"
              style="color: red"
              class="float-center mt-3"
              @click="deleteBankDetails(index)"
              aria-label="Delete bank details button"
            >
              <feather-icon icon="TrashIcon" size="22" />
            </div>
          </b-row>
        </div>
      </div>

      <div v-if="!isDisabled" class="d-flex align-items-center">
        <span
          style="cursor: pointer"
          @click="addBankDetails"
          class="font-weight-medium text-primary"
        >
          <feather-icon :icon="'PlusIcon'" size="12" />
          {{ $t("addBankAccounts") }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import TextInput from "@/components/TextInput.vue";
import MultiSelect from "vue-multiselect";
import { ValidationProvider } from "vee-validate";
import countries from "@/assets/data/countries.json";

export default {
  name: "BankDetails",
  components: {
    TextInput,
    MultiSelect,
    ValidationProvider,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      countries,
    };
  },
  computed: {
    bankDetailsList: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.$emit("input", newValue);
      },
    },
  },
  methods: {
    getBankFields(index) {
      const selected = this.bankDetailsList[index]?.country;
      const country = this.countries.find((c) => c.code === selected?.code);
      return country?.bank_fields || [];
    },
    getDefaultBankDetails() {
      return {
        country: "",
        bankName: "",
        swift: "",
        iban: "",
        routing_number: "",
        account_number: "",
        institution_number: "",
        transit_number: "",
        bsb_code: "",
        branch_code: "",
        bank_code: "",
      };
    },
    addBankDetails() {
      this.bankDetailsList = [...this.bankDetailsList, this.getDefaultBankDetails()];
    },
    async deleteBankDetails(index) {
      const confirmed = await this.showConfirmationDialog({
        title: this.$t("doYouWantToDeleteThisRecord"),
        subText: this.$t("yesDeleteIt"),
      });

      if (confirmed) {
        const newList = [...this.bankDetailsList];
        newList.splice(index, 1);
        this.bankDetailsList = newList;
      }
    },
  },
};
</script>
